import { useState } from "react";
import { Building2, ClipboardList, Eye, BadgePoundSterling, Layers, ShieldCheck, ArrowRight, Loader2, Users } from "lucide-react";
import SEO from "@/components/SEO";
import api from "@/lib/api";
import { toast } from "sonner";

const COMMUNITY = "/images/operators-band.jpg";

const BENEFITS = [
  { i: <ClipboardList/>, t: "Centralised support for resident issues", d: "Every issue logged, tracked and resolved with a clear audit trail." },
  { i: <Layers/>,        t: "Quote and scope review", d: "Specialists check every contractor quote for scope, fairness and necessity." },
  { i: <Users/>,         t: "Reduced friction with residents", d: "Residents feel heard, supported and protected — fewer complaints, faster resolutions." },
  { i: <ShieldCheck/>,   t: "Better contractor accountability", d: "Quote review, scope checks and job sign-off across all your sites." },
  { i: <Eye/>,           t: "Better technical evidence", d: "Photographs, measurements and findings written in language that holds up." },
  { i: <BadgePoundSterling/>, t: "Improved maintenance control", d: "Independent oversight stops over-quoting, scope drift and unnecessary works." },
];

export default function Operators() {
  const [form, setForm] = useState({ company_name: "", contact_name: "", email: "", phone: "", sites_count: 1, message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k,v) => setForm(f=>({...f,[k]:v}));

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/operators/enquiry", { ...form, sites_count: Number(form.sites_count) });
      setDone(true);
      toast.success("Operator enquiry received");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  }

  return (
    <div data-testid="operators-page">
      <SEO
        title="Park Operators | Reduce Cost Drift & Complaints Across Sites"
        description="Structured support, issue triage, quote review and resident-facing guidance for UK park operators. Centralised visibility across every site."
        keywords="park operator support, park home operator service, residential park management UK, park home site operator"
        canonical="https://parkhomesupport.co.uk/operators"
      />
      <section className="relative bg-[#071A33] text-white overflow-hidden">
        <img src={COMMUNITY} className="absolute inset-0 w-full h-full object-cover opacity-20" alt="Park community"/>
        <div className="container-phs section-pad relative">
          <span className="text-[#B88A2A] tracking-[0.22em] uppercase text-xs font-medium">For Park Operators</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 max-w-3xl leading-[1.05]">
            Reduce Complaints, Cost Drift and Maintenance Confusion <span className="text-[#FFE7B6]">Across Your Parks.</span>
          </h1>
          <p className="mt-6 text-lg max-w-2xl text-white/85">
            Park operators need visibility, control and consistency. Park Home Support provides structured support, issue triage, quote review and resident-facing guidance.
          </p>
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="container-phs">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map((b) => (
              <div key={b.t} className="card-default" data-testid={`op-benefit-${b.t.toLowerCase().replace(/[^a-z]+/g,"-")}`}>
                <div className="w-11 h-11 bg-[#064E2E] text-white rounded-xl flex items-center justify-center">{b.i}</div>
                <h3 className="font-display text-xl mt-5 text-[#071A33]">{b.t}</h3>
                <p className="mt-2 text-[#334155] text-[15px] leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad bg-[#F7F4ED]">
        <div className="container-phs max-w-3xl">
          {done ? (
            <div className="card-default text-center" data-testid="op-confirmation">
              <h2 className="font-display text-3xl text-[#071A33]">Operator enquiry received</h2>
              <p className="mt-3 text-[#334155]">We'll be in touch within two business days to discuss support across your sites.</p>
            </div>
          ) : (
            <form className="card-default" onSubmit={onSubmit} data-testid="operator-form">
              <h2 className="font-display text-3xl text-[#071A33]">Discuss Operator Support</h2>
              <p className="mt-2 text-[#334155]">Tell us about your sites — we'll set up a conversation and walk you through the operating model.</p>

              <div className="grid sm:grid-cols-2 gap-5 mt-6">
                <Field label="Company name" required><input className="field-input" required value={form.company_name} onChange={e=>update("company_name", e.target.value)} data-testid="op-company"/></Field>
                <Field label="Contact name" required><input className="field-input" required value={form.contact_name} onChange={e=>update("contact_name", e.target.value)} data-testid="op-contact"/></Field>
                <Field label="Email" required><input className="field-input" type="email" required value={form.email} onChange={e=>update("email", e.target.value)} data-testid="op-email"/></Field>
                <Field label="Phone" required><input className="field-input" required value={form.phone} onChange={e=>update("phone", e.target.value)} data-testid="op-phone"/></Field>
                <Field label="Number of sites"><input className="field-input" type="number" min="1" value={form.sites_count} onChange={e=>update("sites_count", e.target.value)} data-testid="op-sites"/></Field>
              </div>
              <div className="mt-5">
                <label className="field-label">Anything you'd like us to know?</label>
                <textarea className="field-input" value={form.message} onChange={e=>update("message", e.target.value)} data-testid="op-message"/>
              </div>
              <button disabled={submitting} className="btn-primary mt-7" data-testid="op-submit-btn">
                {submitting ? <><Loader2 className="animate-spin" size={16}/> Sending…</> : <>Discuss Operator Support <ArrowRight size={16}/></>}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="field-label">{label}{required ? <span className="text-[#B88A2A]"> *</span> : null}</label>
      {children}
    </div>
  );
}
